import { createEffect, createMemo, createRoot, createSignal } from "solid-js";
import { operator } from "./components/ui.tsx";
import { densityOverride } from "./preferences.ts";
import {
  resolveProfile,
  type ProfileId,
  type ViewProfile,
} from "./profiles.ts";

const STORAGE_KEY = "ep.viewProfile";

function stored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage may be blocked; the default profile applies.
    return null;
  }
}

const state = createRoot(() => {
  const [chosen, setChosen] = createSignal<string | null>(stored());
  // Recomputed when roles arrive or change: a profile no longer offered falls back to the default.
  const profile = createMemo<ViewProfile>(() =>
    resolveProfile(chosen(), operator()),
  );
  createEffect(() => {
    // The person's own choice in the profile wins over the view profile's density.
    document.documentElement.dataset.density =
      densityOverride() ?? profile().density;
  });
  return { profile, setChosen };
});

/** The current view profile (reactive). */
export const viewProfile = state.profile;

/** Switches the profile for this browser; the caller moves to the profile's start screen. */
export function setViewProfile(id: ProfileId): void {
  state.setChosen(id);
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Not persisted; the choice still applies to this session.
  }
}
